import { IcuDiaryItem } from '@/api'
import { Banner, Loader, PrimaryButton } from '@/components'
import { ReactComponent as PenIcon } from '@/assets/pen.svg'
import { useAuthContext } from '@/providers'
import { useIcuDiary } from '@/services'
import { useModal } from '@/utils'
import dayjs from 'dayjs'
import 'dayjs/locale/de'
import { UIEventHandler, useCallback, useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

dayjs.locale('de')

const AuthorBadge = ({ type }: { type?: string }) => {
  const isProvider = type === 'hospital_worker'

  return (
    <div
      className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
        isProvider ? 'bg-[#E8F4F2]' : 'bg-[#FFF0EC]'
      }`}
    >
      {isProvider ? (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path
            d="M8 1C6.34 1 5 2.34 5 4C5 5.66 6.34 7 8 7C9.66 7 11 5.66 11 4C11 2.34 9.66 1 8 1Z"
            fill="#6bbcb1"
          />
          <path
            d="M13 10H12V9C12 8.45 11.55 8 11 8C10.45 8 10 8.45 10 9V10H9C8.45 10 8 10.45 8 11C8 11.55 8.45 12 9 12H10V13C10 13.55 10.45 14 11 14C11.55 14 12 13.55 12 13V12H13C13.55 12 14 11.55 14 11C14 10.45 13.55 10 13 10Z"
            fill="#6bbcb1"
          />
          <path
            d="M7 9C4.79 9 1 10.34 1 13V14C1 14.55 1.45 15 2 15H7.5C7.22 14.39 7 13.72 7 13V9Z"
            fill="#6bbcb1"
          />
        </svg>
      ) : (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path
            d="M8 14.4L7.1 13.58C3.72 10.52 1.6 8.58 1.6 6.2C1.6 4.26 3.14 2.72 5.08 2.72C6.18 2.72 7.24 3.24 8 4.06C8.76 3.24 9.82 2.72 10.92 2.72C12.86 2.72 14.4 4.26 14.4 6.2C14.4 8.58 12.28 10.52 8.9 13.58L8 14.4Z"
            fill="#E8836B"
          />
        </svg>
      )}
    </div>
  )
}

const TimelineDot = ({ isProvider }: { isProvider: boolean }) => (
  <div className="flex flex-col items-center shrink-0 relative">
    <div
      className={`w-3 h-3 rounded-full border-2 z-[1] ${
        isProvider ? 'border-accent bg-[#E8F4F2]' : 'border-[#E8836B] bg-[#FFF0EC]'
      }`}
    />
  </div>
)

const DiaryEntry = ({ entry, isLast }: { entry: IcuDiaryItem; isLast: boolean }) => {
  const openState = useModal(false)
  const date = useMemo(() => dayjs(entry.creation_time), [entry.creation_time])
  const isProvider = entry.author_type === 'hospital_worker'

  return (
    <div className="flex gap-3 relative">
      {/* Timeline connector */}
      <div className="flex flex-col items-center pt-1">
        <TimelineDot isProvider={isProvider} />
        {!isLast && (
          <div className="w-[1.5px] flex-1 bg-gradient-to-b from-sec30 to-transparent min-h-[20px]" />
        )}
      </div>

      {/* Entry card */}
      <div
        className={`flex-1 mb-4 rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 ${
          openState.isOpen
            ? 'shadow-[0_4px_20px_rgba(0,0,0,0.1)]'
            : 'shadow-[0_2px_8px_rgba(0,0,0,0.06)]'
        }`}
        style={{
          background: isProvider
            ? 'linear-gradient(135deg, #FAFEFE 0%, #F0F9F7 100%)'
            : 'linear-gradient(135deg, #FFFBF9 0%, #FFF5F0 100%)',
        }}
        onClick={openState.toggle}
      >
        {/* Date header */}
        <div className="px-4 pt-3 pb-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AuthorBadge type={entry.author_type} />
            <div>
              <div className="text-textS font-semibold text-b100">
                {entry.author_name || (isProvider ? 'Behandelnde:r' : 'Angehörige:r')}
              </div>
              {entry.author_role && <div className="text-[11px] text-b50">{entry.author_role}</div>}
            </div>
          </div>
          <div className="text-right">
            <div className="text-textS font-medium text-sec100">{date.format('dd, DD.MM.YY')}</div>
            <div className="text-[11px] text-b50">{date.format('HH:mm')} Uhr</div>
          </div>
        </div>

        {/* Image */}
        {entry.image && (
          <div className="px-4 pb-2">
            <img
              src={entry.image}
              alt=""
              className={`w-full rounded-xl object-cover transition-all duration-300 ${
                openState.isOpen ? 'max-h-[300px]' : 'max-h-[140px]'
              }`}
            />
          </div>
        )}

        {/* Text content */}
        <div className="px-4 pb-4">
          <div
            className={`text-textS text-b90 leading-relaxed whitespace-pre-wrap transition-all duration-300 ${
              openState.isOpen ? '' : 'line-clamp-3'
            }`}
          >
            {entry.text}
          </div>
          {!openState.isOpen && entry.text.length > 120 && (
            <div className="text-[12px] text-primary font-medium mt-1">Weiterlesen...</div>
          )}
        </div>
      </div>
    </div>
  )
}

const EmptyState = ({ onAdd }: { onAdd: () => void }) => {
  const { t } = useTranslation()
  const { currentPatient } = useAuthContext()

  return (
    <div className="max-w-[488px] p-4 w-full flex flex-col gap-4 !h-full">
      <Banner
        title={t('icuDiaryPage.banner.title')}
        actions={[
          {
            text: t('icuDiaryPage.banner.button'),
            variant: 'contained',
            onClick: onAdd,
          },
        ]}
      >
        <Trans
          i18nKey="icuDiaryPage.banner.text"
          values={{
            patientName: `${currentPatient?.first_name} ${currentPatient?.last_name}`,
            patientFirstName: currentPatient?.first_name,
          }}
        />
      </Banner>
    </div>
  )
}

const DateSeparator = ({ date }: { date: string }) => (
  <div className="flex items-center gap-3 my-2">
    <div className="h-[1px] flex-1 bg-gradient-to-r from-transparent to-sec10" />
    <span className="text-[12px] font-semibold text-sec50 uppercase tracking-wider">
      {dayjs(date).format('dddd, DD. MMMM YYYY')}
    </span>
    <div className="h-[1px] flex-1 bg-gradient-to-l from-transparent to-sec10" />
  </div>
)

type DiaryTimelineProps = {
  onAdd: () => void
}

export const DiaryTimeline = ({ onAdd }: DiaryTimelineProps) => {
  const { t } = useTranslation()
  const { patientId } = useParams<{ patientId: string }>()
  const { currentPatient } = useAuthContext()

  const diaryResponse = useIcuDiary(patientId!)
  const { data = [], isFetchingNextPage, hasNextPage, fetchNextPage, isFetched } = diaryResponse

  const scrollHandler = useCallback<UIEventHandler<HTMLDivElement>>(
    (e) => {
      const possibleScroll = e.currentTarget.scrollHeight - e.currentTarget.offsetHeight
      const scrollBottom = possibleScroll - e.currentTarget.scrollTop

      if (scrollBottom < 400 && hasNextPage && !isFetchingNextPage) {
        fetchNextPage()
      }
    },
    [hasNextPage, isFetchingNextPage],
  )

  // Group entries by date
  const groupedEntries = useMemo(() => {
    const groups: { date: string; entries: IcuDiaryItem[] }[] = []
    let currentDate = ''

    // API-Reihenfolge beibehalten (neueste zuerst): die Pagination laedt beim
    // Scrollen nach unten aeltere Eintraege nach — eine Aufsteigend-Sortierung
    // wuerde nachgeladene Seiten OBERHALB des Viewports einsortieren und die
    // Timeline springen lassen.
    data.forEach((entry) => {
      const entryDate = dayjs(entry.creation_time).format('YYYY-MM-DD')
      if (entryDate !== currentDate) {
        currentDate = entryDate
        groups.push({ date: entryDate, entries: [entry] })
      } else {
        groups[groups.length - 1].entries.push(entry)
      }
    })

    return groups
  }, [data])

  if (!isFetched) return <Loader />

  if (!data.length) return <EmptyState onAdd={onAdd} />

  return (
    <div className="flex flex-col h-full w-full max-w-[488px] p-4">
      {/* Add button */}
      {currentPatient && !currentPatient.is_patient_user && (
        <PrimaryButton block className="svg:active:stroke-primary shrink-0" onClick={onAdd}>
          <PenIcon width={20} height={20} className="stroke-b0 !fill-primary" />
          {t('icuDiaryPage.button')}
        </PrimaryButton>
      )}

      {/* Confidentiality notice */}
      <div className="text-textXS text-b50 text-center py-2 shrink-0">
        {t(
          'icuDiaryPage.confidentialityNote',
          'Ihre Einträge sind vertraulich – das Behandlungsteam kann Ihre persönlichen Einträge nicht sehen.',
        )}
      </div>

      {/* Timeline scroll area */}
      <div className="flex-1 overflow-y-auto" onScroll={scrollHandler}>
        {groupedEntries.map((group, groupIdx) => (
          <div key={group.date}>
            <DateSeparator date={group.date} />
            {group.entries.map((entry, idx) => (
              <DiaryEntry
                key={entry.id}
                entry={entry}
                isLast={groupIdx === groupedEntries.length - 1 && idx === group.entries.length - 1}
              />
            ))}
          </div>
        ))}

        {hasNextPage && (
          <div className="h-[80px] flex items-center justify-center">
            <Loader size={32} />
          </div>
        )}
      </div>
    </div>
  )
}
